/**
 * The one place an app instance is created.
 *
 * Under vue 2 a `new Vue({...})` root picked the store and the router up from options that
 * a global plugin had installed on every instance. Pinia and vue-router 4 install onto an
 * app instead - `app.use(pinia)` - so a root that is not built here has no `$pinia` and no
 * `$router`, however the rest of the app is wired.
 *
 * Nothing is inherited any more either. Components, directives and the `$`-prefixed instance
 * properties used to be registered on the compat `Vue` global, which @vue/compat copied into
 * every app it built - the vue 2 global API, and the last thing keeping GLOBAL_PROTOTYPE on.
 * They are one plugin now (vue/globalRegistrations.js), registered here at bootstrap like
 * any other, so an app built through this function is the only kind that has them.
 */
import { createApp as createVueApp } from 'vue';
import pinia from '~/angular/app/pinia';

// The router is not imported here on purpose: it pulls in the whole route table, and the
// route components import this module back through simplePopupFactory. spa/mount registers
// it instead, before it mounts anything, so every app built afterwards - the detached
// popups included - gets it too.
const appPlugins = [pinia];

export function registerAppPlugin (plugin) {
    if (!appPlugins.includes(plugin)) {
        appPlugins.push(plugin);
    }
}

export function createApp (rootComponent, rootProps) {
    const app = createVueApp(rootComponent, rootProps);
    for (const plugin of appPlugins) {
        app.use(plugin);
    }
    return app;
}

export default createApp;
